/**
 * Inspector-row summary glyph helpers (Phase 3 of pb-section-properties
 * overhaul). Each returns an HTML *fragment* destined for the right side of
 * a `pb-spi-row` (the "summary glyph" slot).
 *
 * No DOM mutation — pure templates so callers can re-render after every
 * `state-changed` without juggling event listeners.
 */

import {
  listDecorativeSvgs,
  serializeDecorativePreview
} from '../../editing/insert-decorative-svg.js';
import { validateSectionCSS } from '../../editing/code-view-editor.js';
import { sectionHasForm } from '../form-builder.js';
import { SPACING_PRESETS } from '../../shared/spacing-presets.js';
import { escapeHTML as escapeHtml } from '../../shared/html-escape.js';

const customCssValidityByInstance = new Map();

function escapeAttr(value) {
  return escapeHtml(value);
}

/**
 * Background summary: swatch dot + value label. When the override matches a
 * known spacing preset / theme color (rare for bg) we'd show its name; for
 * Phase 3 we show the hex (or "Default") since theme-token mapping lives
 * in the Background drawer (Phase 4).
 */
export function renderBackgroundSummary(overrides) {
  const value = overrides && overrides.backgroundColor ? String(overrides.backgroundColor) : '';
  if (!value) {
    return `<span class="pb-spi-summary pb-spi-summary-default">Default</span>`;
  }
  const safe = escapeAttr(value);
  return `
    <span class="pb-spi-summary pb-spi-summary-bg">
      <span class="pb-spi-bg-swatch" style="background:${safe}" aria-hidden="true"></span>
      <span class="pb-spi-summary-text">${escapeHtml(value)}</span>
    </span>
  `;
}

/**
 * Spacing summary: a tiny 4-bar diagram (mT · pT · pB · mB) plus a textual
 * shorthand. Heights scale from 0 to 12px proportional to value (clamps and
 * unparseable values cap at the median).
 */
export function renderSpacingSummary(overrides) {
  const fields = ['marginTop', 'paddingTop', 'paddingBottom', 'marginBottom'];
  const labels = { marginTop: 'mT', paddingTop: 'pT', paddingBottom: 'pB', marginBottom: 'mB' };
  const rems = fields.map((f) => parseRemSafe(overrides && overrides[f]));
  const max = Math.max(0.001, ...rems);
  const heights = rems.map((r) => {
    if (max <= 0) return 2;
    const pct = Math.min(1, r / max);
    return 2 + pct * 10; // 2px–12px
  });
  const anySet = rems.some((v) => v > 0);

  const bars = fields
    .map((f, i) => {
      const safe = escapeHtml(formatRem(rems[i]));
      return `<span class="pb-spi-spacing-bar" style="height:${heights[i].toFixed(1)}px" data-tooltip="${labels[f]}: ${safe}" aria-hidden="true"></span>`;
    })
    .join('');

  const text = anySet ? rems.map(formatRem).join(' / ') : 'Default';

  return `
    <span class="pb-spi-summary pb-spi-summary-spacing" data-tooltip="Margin Top / Padding Top / Padding Bottom / Margin Bottom">
      <span class="pb-spi-spacing-bars" aria-hidden="true">${bars}</span>
      <span class="pb-spi-summary-text">${escapeHtml(text)}</span>
    </span>
  `;
}

export function setCustomCssSummaryValidity(instanceId, validity) {
  if (!instanceId) return;
  if (validity && typeof validity.ok === 'boolean') {
    customCssValidityByInstance.set(instanceId, {
      ok: validity.ok,
      message: validity.message || '',
      lineCount: Number.isFinite(validity.lineCount) ? validity.lineCount : null
    });
  } else {
    customCssValidityByInstance.delete(instanceId);
  }
}

/** Columns summary: shows the active count or `Default`. */
export function renderColumnsSummary(overrides, sourceItemCount = null) {
  const raw = overrides && overrides.columnCount;
  const n = raw == null || raw === '' ? null : Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) {
    if (Number.isFinite(sourceItemCount)) {
      return `<span class="pb-spi-summary pb-spi-summary-default">${sourceItemCount} ${sourceItemCount === 1 ? 'column' : 'columns'}</span>`;
    }
    return `<span class="pb-spi-summary pb-spi-summary-default">Default</span>`;
  }
  return `<span class="pb-spi-summary">${n} ${n === 1 ? 'column' : 'columns'}</span>`;
}

/** Custom-CSS summary: line count + tiny validity badge. */
export function renderCustomCssSummary(overrides, instanceId = '') {
  const css = overrides && overrides.customCSS ? String(overrides.customCSS) : '';
  if (!css.trim()) {
    return `<span class="pb-spi-summary pb-spi-summary-default">Default</span>`;
  }
  const cachedValidity = instanceId ? customCssValidityByInstance.get(instanceId) : null;
  const lines = cachedValidity?.lineCount || css.split(/\r?\n/).length;
  const validity = cachedValidity || validateSectionCSS(css);
  const ok = validity?.ok !== false;
  const badge = ok ? '✓' : '✗';
  const label = ok ? 'CSS valid' : validity?.message || 'CSS invalid';
  return `
    <span class="pb-spi-summary pb-spi-summary-css" data-tooltip="${escapeAttr(label)}">
      <span class="pb-spi-summary-text">${lines} line${lines === 1 ? '' : 's'}</span>
      <span class="pb-spi-css-validity ${ok ? 'pb-spi-css-valid' : 'pb-spi-css-invalid'}" aria-label="${escapeAttr(label)}">${badge}</span>
    </span>
  `;
}

/** Decorative SVG summary: count + up to 3 inline thumb chips, `+N` for the rest. */
export function renderDecorativeSvgSummary(section) {
  let items = [];
  try {
    items = section ? listDecorativeSvgs(section.html) || [] : [];
  } catch {
    items = [];
  }
  if (!items.length) {
    return `<span class="pb-spi-summary pb-spi-summary-default">None</span>`;
  }
  const visible = items.slice(0, 3);
  const overflow = items.length - visible.length;
  const chips = visible
    .map((it) => {
      const preview = renderDecorativePreviewChip(it);
      return `<span class="pb-spi-svg-chip" aria-hidden="true">${preview}</span>`;
    })
    .join('');
  const overflowChip =
    overflow > 0
      ? `<span class="pb-spi-svg-chip pb-spi-svg-chip-overflow" aria-hidden="true">+${overflow}</span>`
      : '';
  return `
    <span class="pb-spi-summary pb-spi-summary-svg">
      <span class="pb-spi-svg-chips">${chips}${overflowChip}</span>
      <span class="pb-spi-summary-text">${items.length}</span>
    </span>
  `;
}

function renderDecorativePreviewChip(item) {
  if (!item) return 'SVG';
  if (item.tagName === 'img' && item.src) {
    const src = String(item.src || '').trim();
    if (isSafePreviewSrc(src)) {
      return `<img src="${escapeAttr(src)}" alt="" loading="lazy" />`;
    }
    return 'IMG';
  }
  const preview = serializeDecorativePreview(item.markup || '');
  return preview || 'SVG';
}

function isSafePreviewSrc(src) {
  if (!src) return false;
  const lower = src.trim().toLowerCase();
  if (lower.startsWith('javascript:')) return false;
  if (lower.startsWith('data:') && !lower.startsWith('data:image/')) return false;
  return true;
}

/** Form summary: field count, when the section has a form. */
export function renderFormSummary(section) {
  if (!section || !sectionHasForm(section.html)) return '';
  const count = countFormFields(section.html);
  return `
    <span class="pb-spi-summary pb-spi-summary-form">
      <span class="pb-spi-summary-text">${count} field${count === 1 ? '' : 's'}</span>
    </span>
  `;
}

// ── internals ────────────────────────────────────────────────────────────

function parseRemSafe(value) {
  if (!value) return 0;
  const str = String(value).trim();
  // Pull a numeric rem out of `1.5rem` or `clamp(1rem, 2vw, 3rem)` middle term.
  const remMatch = str.match(/^(-?[\d.]+)\s*rem$/);
  if (remMatch) return Math.max(0, parseFloat(remMatch[1]) || 0);
  // Resolve known SPACING_PRESETS by name → middle term.
  const preset = SPACING_PRESETS.find((p) => p.value === str);
  if (preset && preset.value) {
    const mid = preset.value.match(/clamp\([^,]*,\s*[^,]*,\s*([-\d.]+)rem\)/);
    if (mid) return Math.max(0, parseFloat(mid[1]) || 0);
    const flat = preset.value.match(/^(-?[\d.]+)rem$/);
    if (flat) return Math.max(0, parseFloat(flat[1]) || 0);
  }
  // Fallback: try to extract the LAST number in the string.
  const lastNum = str.match(/(-?[\d.]+)\s*rem/g);
  if (lastNum && lastNum.length) {
    const m = lastNum[lastNum.length - 1].match(/(-?[\d.]+)/);
    if (m) return Math.max(0, parseFloat(m[1]) || 0);
  }
  return 0;
}

function formatRem(rem) {
  if (!Number.isFinite(rem) || rem === 0) return '0';
  // Trim trailing zeros: 1.0 → "1", 1.25 → "1.25"
  const rounded = Math.round(rem * 100) / 100;
  return `${rounded}`;
}

function countFormFields(html) {
  if (!html || typeof DOMParser === 'undefined') return 0;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const forms = doc.querySelectorAll('form');
    let total = 0;
    for (const f of forms) {
      total += f.querySelectorAll('input, textarea, select').length;
    }
    return total;
  } catch {
    return 0;
  }
}
